import { useEffect, useMemo, useState } from "react";
import { Outlet, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { client } from "@/lib/api";
import { qk } from "@/lib/query-keys";
import { useOrgSocket } from "@/lib/socket";
import { OrgContext, type OrgContextValue } from "@/lib/OrgContext";
import { Sidebar } from "@/components/Sidebar";
import { CommandPalette } from "@/components/CommandPalette";
import type { OrgMember } from "@rivets/core";
import { ArchiveToast } from "@/components/ArchiveToast";
import { NoticeToast } from "@/components/NoticeToast";
import { syncKeyboardShortcutSettings, useAppKeyboardShortcuts, useShortcutCommand } from "@/lib/keyboardShortcuts";
import { KeyboardShortcutsModal } from "@/components/KeyboardShortcutsModal";
import { AssistantDrawer } from "@/features/assistant/AssistantDrawer";
import { useSidebarLayout } from "@/lib/sidebarLayout";
import { useFileDrafts } from "@/features/files/fileDraftStore";
import { activateDrafts, useDraftScope } from "@/features/chat/draftPersistence";
import { ErrorBoundary } from "@/components/ErrorBoundary";

export function OrgShell() {
  const { orgId } = useParams({ strict: false }) as { orgId: string };
  const connect = useOrgSocket((s) => s.connect);
  const disconnect = useOrgSocket((s) => s.disconnect);
  const accessReloading = useOrgSocket((s) => s.accessReloading);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const meQuery = useQuery({ queryKey: qk.me(), queryFn: ({ signal }: { signal: AbortSignal }) => client.withSignal(signal).me() });
  useAppKeyboardShortcuts();
  const { collapsed, toggle: toggleSidebar } = useSidebarLayout();
  useShortcutCommand("toggleSidebar", toggleSidebar);
  useShortcutCommand("showKeyboardShortcuts", () => {
    setShortcutsOpen(true);
    const userId = meQuery.data?.user.id;
    if (userId) {
      void client.keyboardShortcutPreferences()
        .then((response) => syncKeyboardShortcutSettings(userId, response.keyboardShortcuts))
        .catch(() => { /* Keep the account-scoped offline cache active. */ });
    }
  });

  const userId = meQuery.data?.user.id;
  useEffect(() => { if (userId) useFileDrafts.getState().scopeTo(userId); }, [userId]);
  // Unsent work is this member's in this organization (CONTRACT C3). A page
  // load primes it before the first render (`main.tsx`); an organization
  // switch loads it here, after the old organization's composers have parked
  // what they held — a passive effect, so it runs after their unmount.
  const draftScope = useDraftScope();
  useEffect(() => { if (userId && orgId) activateDrafts(userId, orgId, { confirmed: true }); }, [userId, orgId]);
  // A composer reads its parked tray when it mounts, so the page waits for
  // the organization's drafts — only ever a frame, on a switch.
  const draftsReady = draftScope.orgId === orgId && (!userId || draftScope.userId === userId) || meQuery.isError;

  const orgDetailQuery = useQuery({
    queryKey: qk.org(orgId),
    queryFn: ({ signal }: { signal: AbortSignal }) => client.withSignal(signal).orgDetail(orgId),
    enabled: !!orgId,
  });

  useEffect(() => {
    if (!orgId) return;
    connect(orgId);
    return () => disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  useEffect(() => {
    if (!meQuery.data) return;
    void syncKeyboardShortcutSettings(meQuery.data.user.id, meQuery.data.keyboardShortcuts).catch(() => {
      // Account-scoped local cache remains active and will retry on the next bootstrap.
    });
  }, [meQuery.data]);

  const membersById = useMemo(() => {
    const map = new Map<string, OrgMember>();
    for (const m of orgDetailQuery.data?.members ?? []) map.set(m.userId, m);
    return map;
  }, [orgDetailQuery.data?.members]);

  const ctxValue: OrgContextValue = {
    orgId,
    org: orgDetailQuery.data?.org,
    members: orgDetailQuery.data?.members ?? [],
    role: orgDetailQuery.data?.role,
    membersById,
  };

  const orgName = ctxValue.org?.name ?? (orgDetailQuery.isError ? "Organization" : "Loading…");

  if (accessReloading) return <main className="page" role="status"><p>Your access changed. Refreshing your workspace…</p><button className="btn" onClick={() => window.location.reload()}>Reload workspace</button></main>;

  return (
    <OrgContext.Provider value={ctxValue}>
      {/* The collapsed flag is a desktop-only layout decision, so it is an
          attribute the stylesheet reads rather than a mount decision: the
          sidebar stays mounted (it owns ⌘N, the task number row and the new-task
          modal) and the mobile drawer keeps working exactly as before. */}
      <div className="app-shell" data-sidebar-collapsed={collapsed ? "true" : "false"}>
        <div className="mobile-topbar">
          <button
            className="mobile-menu-button"
            type="button"
            aria-label="Open navigation"
            aria-expanded={sidebarOpen}
            onClick={() => setSidebarOpen(true)}
          >
            <span aria-hidden="true">☰</span>
          </button>
          <span className="mobile-org-name truncate">{orgName}</span>
        </div>
        <ErrorBoundary name="sidebar" fallback={({ reset }) => (
          <aside className="sidebar sidebar-error" aria-label="Primary navigation">
            <div className="error-boundary" role="alert">
              <div className="error-state-title">The sidebar could not be shown.</div>
              <button type="button" className="btn btn-sm error-state-action" onClick={reset}>Try again</button>
            </div>
          </aside>
        )}>
          <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        </ErrorBoundary>
        {sidebarOpen && (
          <button
            className="sidebar-scrim"
            type="button"
            aria-label="Close navigation"
            onClick={() => setSidebarOpen(false)}
          />
        )}
        <div className="main-content">
          {draftsReady ? <Outlet /> : null}
        </div>
        {/* Mounted once, and kept mounted while closed: the open conversation
            keeps its chat subscription, so a reply can land (and raise the
            sidebar's unread dot) while you are anywhere else in the org. */}
        <ErrorBoundary name="assistant" fallback={() => null}>
          <AssistantDrawer memberId={meQuery.data?.user.id} />
        </ErrorBoundary>
      </div>
      <CommandPalette currentOrgId={orgId} />
      <KeyboardShortcutsModal open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
      <ArchiveToast />
      <NoticeToast />
    </OrgContext.Provider>
  );
}
